// Assess Finance Charges (desktop Customers ▸ Assess Finance Charges + Preferences ▸ Finance Charge).
// Settings: annual rate %, minimum charge (with rate 0 = a flat late fee), grace days, the income account.
// Preview who is overdue past the grace days, untick anyone to skip, then assess — each becomes a charge on the
// family's account (shows on statements, paid with Receive payment).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { money, toCents, fmtDate } from '../money';
import { Combo } from './FormFrame';
import { Percent, CheckCircle2 } from 'lucide-react';

const CARD = 'bg-white text-slate-800 border-slate-200';
const field = 'h-9 rounded border border-slate-300 bg-white px-2 text-sm text-slate-800';
const errOf = (e) => e?.response?.data?.detail || 'Request failed';
const today = () => new Date().toISOString().slice(0, 10);

export function FinanceChargesPage({ base, go }) {
  const [prefs, setPrefs] = useState(null);
  const [accts, setAccts] = useState([]);
  const [asOf, setAsOf] = useState(today());
  const [prev, setPrev] = useState(null);
  const [skip, setSkip] = useState({});
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [done, setDone] = useState(null);
  useEffect(() => {
    axios.get(`${base}/ar/preferences`).then((r) => setPrefs({
      fin_rate_annual: r.data?.fin_rate_annual ?? 0, fin_min_charge: ((r.data?.fin_min_charge || 0) / 100).toFixed(2),
      fin_grace_days: r.data?.fin_grace_days ?? 0, fin_account_id: r.data?.fin_account_id || '' })).catch((e) => setErr(errOf(e)));
    axios.get(`${base}/accounts`).then((r) => setAccts((r.data?.accounts || []).filter((a) => a.is_active && ['Income', 'Other Income'].includes(a.type))
      .map((a) => ({ id: a.account_id, label: a.full_name || a.name })))).catch(() => {});
  }, [base]);
  const preview = useCallback(async () => {
    setErr(''); setDone(null);
    try { setPrev((await axios.get(`${base}/finance-charges`, { params: { as_of: asOf } })).data); setSkip({}); }
    catch (e) { setErr(errOf(e)); }
  }, [base, asOf]);
  const saveSettings = async () => {
    setErr('');
    try {
      await axios.put(`${base}/ar/preferences`, { fin_rate_annual: Number(prefs.fin_rate_annual) || 0, fin_min_charge: toCents(prefs.fin_min_charge || '0'),
        fin_grace_days: parseInt(prefs.fin_grace_days, 10) || 0, fin_account_id: prefs.fin_account_id || null });
      await preview();
    } catch (e) { setErr(errOf(e)); }
  };
  const assess = async () => {
    const ids = (prev?.rows || []).filter((r) => !skip[r.customer_id]).map((r) => r.customer_id);
    if (!ids.length) return;
    setBusy(true); setErr('');
    try { setDone((await axios.post(`${base}/finance-charges`, { as_of: asOf, customer_ids: ids })).data); setPrev(null); }
    catch (e) { setErr(errOf(e)); } finally { setBusy(false); }
  };
  if (!prefs) return <div className="text-slate-500">Loading…</div>;
  const sp = (k) => (e) => setPrefs({ ...prefs, [k]: e.target.value });
  const picked = (prev?.rows || []).filter((r) => !skip[r.customer_id]);
  return (
    <div className="space-y-4 text-slate-800 max-w-5xl" data-testid="finance-charges-page">
      <h2 className="text-xl font-semibold flex items-center gap-2"><Percent className="h-5 w-5 text-emerald-600" />Assess finance charges</h2>
      {err && <div className="rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-700">{err}</div>}
      <div className={`rounded-lg border p-3 ${CARD}`}>
        <h3 className="font-semibold mb-2">Finance charge settings</h3>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-500">Annual rate %<Input className={`${field} w-24`} value={prefs.fin_rate_annual} onChange={sp('fin_rate_annual')} data-testid="fc-rate" /></label>
          <label className="text-xs text-slate-500">Minimum charge<Input className={`${field} w-28`} value={prefs.fin_min_charge} onChange={sp('fin_min_charge')} data-testid="fc-min" /></label>
          <label className="text-xs text-slate-500">Grace days<Input className={`${field} w-20`} value={prefs.fin_grace_days} onChange={sp('fin_grace_days')} data-testid="fc-grace" /></label>
          <label className="text-xs text-slate-500 min-w-[260px]">Finance charge account<Combo value={prefs.fin_account_id} onChange={(v) => setPrefs({ ...prefs, fin_account_id: v })} options={accts} placeholder="Income account" testid="fc-acct" /></label>
          <Button variant="outline" onClick={saveSettings} data-testid="fc-save-settings">Save settings</Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Rate 0 with a minimum charge = a flat late fee per overdue family. Charges start after the due date plus the grace days, and run from the last assessment.</p>
      </div>
      <div className={`rounded-lg border p-3 ${CARD}`}>
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <label className="text-xs text-slate-500">Assessment date<Input type="date" className={field} value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="fc-asof" /></label>
          <Button variant="outline" onClick={preview} data-testid="fc-preview">Preview</Button>
          {prev && <Button className="bg-emerald-600 text-white" disabled={busy || !picked.length || !prefs.fin_account_id} onClick={assess} data-testid="fc-assess">
            {busy ? 'Assessing…' : `Assess ${picked.length} charge(s) · ${money(picked.reduce((s, r) => s + r.charge, 0))}`}</Button>}
        </div>
        {done && <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="fc-done"><CheckCircle2 className="h-4 w-4" />Assessed {done.assessed} finance charge(s), {money(done.total)}.
          <button className="text-emerald-600 hover:underline" onClick={() => go('form', 'statement')}>Send statements</button></p>}
        {prev && (prev.rows.length === 0 ? <p className="text-sm text-slate-500">No one is overdue past the grace days on {fmtDate(prev.as_of)}.</p> : (
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full text-sm" data-testid="fc-table">
              <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-1.5 w-8" /><th className="p-1.5">Customer</th><th className="p-1.5">Overdue invoices</th><th className="p-1.5 text-right">Overdue balance</th><th className="p-1.5 text-right">Finance charge</th></tr></thead>
              <tbody>
                {prev.rows.map((r) => (
                  <tr key={r.customer_id} className="border-b border-slate-100" data-testid={`fc-row-${r.customer_id}`}>
                    <td className="p-1.5"><input type="checkbox" checked={!skip[r.customer_id]} onChange={(e) => setSkip({ ...skip, [r.customer_id]: !e.target.checked })} /></td>
                    <td className="p-1.5">{r.name}</td>
                    <td className="p-1.5 text-slate-500">{r.invoices.map((i) => `#${i.num || '—'} (${i.days} days)`).join(', ')}</td>
                    <td className="p-1.5 text-right tabular-nums">{money(r.overdue_balance)}</td>
                    <td className="p-1.5 text-right tabular-nums font-medium">{money(r.charge)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
}
